import { useQuery } from "@apollo/client/react";
import { yupResolver } from "@hookform/resolvers/yup";
import useAddressForm from "@repo/components/src/forms/address/useAddressForm";
import { TaxProfileFragmentFragment } from "@repo/graphql/documents/fiscal/fragment.generated";
import { TaxProfileRequirementsDocument } from "@repo/graphql/documents/fiscal/query.generated";
import {
  TaxProfileFieldKeyEnum,
  TaxProfileOwnerKindEnum,
} from "@repo/graphql/gql/graphql";
import { useEffect, useMemo, useRef } from "react";
import { Resolver, useForm } from "react-hook-form";

import { addressFormValuesFromSaved, buildAddressInput } from "./address";
import { buildDefaultValues } from "./defaults";
import { findField, findRegime, hasField, selectActiveFields } from "./fields";
import { buildTaxProfileInput } from "./input";
import { AcknowledgmentRule, buildTaxProfileSchema } from "./schema";
import { mapTaxProfileUserErrors } from "./serverErrors";
import type { TaxProfileFormValues } from "./types";
import { useRegistryMatchState } from "./useRegistryMatchState";

type Options = {
  countryCode: string;
  ownerKind: TaxProfileOwnerKindEnum;
  initial?: TaxProfileFragmentFragment | null;
  defaultLegalName: string;
  defaultAddressLine?: string;
  defaultTaxId?: string;
  acknowledgment: AcknowledgmentRule;
};

type UserErrorLike = { field: string[]; message: string };

/**
 * The state of a tax profile form that renders whatever the country's policy
 * describes: the requirements, the active field list (which follows the
 * selected regime), the form and its address form, and the mapping of the
 * values to the mutation input and of the server errors back to the inputs.
 */
export function useTaxProfileForm({
  countryCode,
  ownerKind,
  initial,
  defaultLegalName,
  defaultAddressLine,
  defaultTaxId,
  acknowledgment,
}: Options) {
  const { data, loading } = useQuery(TaxProfileRequirementsDocument, {
    variables: { countryCode, ownerKind },
  });
  const requirements = data?.fiscal.taxProfileRequirements ?? null;
  const regimes = useMemo(() => requirements?.regimes ?? [], [requirements]);
  const requirementsFields = useMemo(
    () => requirements?.fields ?? [],
    [requirements],
  );

  // The schema depends on the active fields, which depend on the selected
  // regime, which lives in the form. The resolver reads the latest schema
  // through a ref to break that cycle.
  const schemaRef = useRef<ReturnType<typeof buildTaxProfileSchema> | null>(
    null,
  );
  const resolver: Resolver<TaxProfileFormValues> = (
    values,
    context,
    options,
  ) =>
    schemaRef.current
      ? yupResolver(schemaRef.current)(values, context, options)
      : { values, errors: {} };

  const formProps = useForm<TaxProfileFormValues>({
    mode: "onBlur",
    resolver,
    defaultValues: buildDefaultValues(initial, {
      legalName: defaultLegalName,
      addressLine: defaultAddressLine,
      taxId: defaultTaxId,
    }),
  });

  const selectedRegime = findRegime(regimes, formProps.watch("taxRegime"));
  const fields = selectActiveFields(requirementsFields, selectedRegime);

  const schema = useMemo(
    () => buildTaxProfileSchema(fields, regimes, acknowledgment),
    [fields, regimes, acknowledgment],
  );
  useEffect(() => {
    schemaRef.current = schema;
  }, [schema]);

  const addressField = findField(fields, TaxProfileFieldKeyEnum.Address);
  const savedAddressValues = useMemo(
    () => addressFormValuesFromSaved(initial?.address),
    [initial],
  );
  const addressForm = useAddressForm(
    addressField ? countryCode : null,
    savedAddressValues,
  );
  const registryMatch = useRegistryMatchState(defaultLegalName);

  /** Validates the address form; an optional address left blank passes. */
  const validateAddress = async (): Promise<boolean> => {
    if (!addressField) return true;
    const input = buildAddressInput(
      addressForm.formProps.getValues(),
      addressForm.format,
      addressField.required,
    );
    if (!input) {
      addressForm.formProps.clearErrors();
      return true;
    }
    return addressForm.formProps.trigger();
  };

  const buildInput = (values: TaxProfileFormValues) =>
    buildTaxProfileInput(
      fields,
      values,
      addressField
        ? buildAddressInput(
            addressForm.formProps.getValues(),
            addressForm.format,
            addressField.required,
          )
        : undefined,
    );

  /** Shows the `userErrors` of a save under their inputs; returns those left for a toast. */
  const applyUserErrors = (userErrors: UserErrorLike[]): string[] => {
    const { identifierMessage, fieldErrors, toastMessages } =
      mapTaxProfileUserErrors(userErrors, fields);
    if (identifierMessage) {
      formProps.setError("taxId", {
        type: "server",
        message: identifierMessage,
      });
    }
    fieldErrors.forEach(({ name, message }) =>
      formProps.setError(name, { type: "server", message }),
    );
    return toastMessages;
  };

  return {
    loading,
    isSupported: requirements?.isSupported ?? true,
    unsupportedMessage: requirements?.unsupportedMessage ?? "",
    formProps,
    schema,
    regimes,
    selectedRegime,
    fields,
    hasBusinessActivityField: hasField(
      fields,
      TaxProfileFieldKeyEnum.BusinessActivity,
    ),
    address: addressForm,
    registryMatch,
    validateAddress,
    buildInput,
    applyUserErrors,
  };
}

export type TaxProfileFormState = ReturnType<typeof useTaxProfileForm>;
